<template>
  <RepositoryWrapper>
    <template
      #default="{
        items,
        isActionsAllowed,
        handleClickItem,
        handleExport,
        handleClone,
        repositoryId,
        search,
        updateSearch,
        filter,
        filterFields,
        updateFilter,
      }"
    >
      <UIDialog
        :is-open
        :z-index
        size="2md"
        title="Repositories"
        class="modal-repositories"
        @close="handleClose"
      >
        <template #title>
          <div class="modal-repositories__title">
            <span>Repositories</span>
            <RepositoriesActions
              :disabled="!isActionsAllowed && !isNextPlanAvailable"
              :unavailable="!isActionsAllowed && isNextPlanAvailable"
              size="xs"
              @close="handleClose"
            >
              <slot name="actions" />
            </RepositoriesActions>
          </div>
        </template>
        <div class="modal-repositories__search">
          <div class="modal-repositories__search-input">
            <UIPanelSearch
              :model-value="search"
              placeholder="Search repositories..."
              @update:model-value="updateSearch"
            />
          </div>
          <UIFilter
            :model-value="filter"
            label=""
            :fields="filterFields"
            flat
            @update:model-value="updateFilter"
          />
        </div>
        <UILayoutNoData
          v-if="!items.length"
          inline
          hide-question-icon
          class="modal-repositories__no-data"
        />
        <div v-else class="modal-repositories__items">
          <RepositoriesItem
            v-for="item in items"
            :key="item.id"
            :item
            :active="item.id === repositoryId"
            @delete="handleDelete"
            @unlink="handleUnlink"
            @click="(handleClickItem(item), handleClose())"
            @export="handleExport"
            @clone="handleClone"
            @close="handleClose"
          />
        </div>
        <RepositoriesLimit v-if="!isActionsAllowed" @click="handleClickUpgrade" />
        <RepositoriesDelete
          v-if="deleteRepository"
          :repository="deleteRepository"
          @cancel="handleCancelDelete"
          @export="handleExport"
          @deleted="handleDeleted"
        />
        <RepositoriesUnlink
          v-if="unlinkRepository"
          :repository="unlinkRepository"
          @cancel="handleCancelUnlink"
          @unlinked="handleUnlinked"
        />
      </UIDialog>
    </template>
  </RepositoryWrapper>
</template>

<script setup lang="ts">
import { computed, provide, ref } from 'vue'

import { ModalClass, Repository } from '@types'

import useAnalyticsStore from '@/store/analytics'
import { useMainStore } from '@/store/main'
import { useRepositoriesStore } from '@/store/repositories/index'
import { useUserSubscriptionsStore } from '@/store/user/subscriptions'

import RepositoryWrapper from '../RepositoryWrapper.vue'
import RepositoriesActions from './RepositoriesActions.vue'
import RepositoriesDelete from './RepositoriesDelete.vue'
import RepositoriesUnlink from './RepositoriesUnlink.vue'
import RepositoriesItem from './RepositoriesItem.vue'
import RepositoriesLimit from './RepositoriesLimit.vue'
import { UILayoutNoData, UIPanelSearch } from '@ui/core'
import { UIFilter } from '@ui/controllers'
import { UIDialog } from '@ui/modals'

type Props = {
  modal: ModalClass<any>
}

const props = defineProps<Props>()

const analyticsStore = useAnalyticsStore()
const repositoriesStore = useRepositoriesStore()
const mainStore = useMainStore()
const userSubscriptionsStore = useUserSubscriptionsStore()

provide('size', 'sm')

const deleteRepository = ref<Repository>()
const unlinkRepository = ref<Repository>()

const isOpen = computed(() => props.modal.isOpened)
const zIndex = computed(() => props.modal.zIndex)

const isNextPlanAvailable = computed(() => {
  return !!userSubscriptionsStore.getNextPlan
})

const handleClose = () => {
  props.modal.close()
}

const handleDelete = (item: Repository) => {
  deleteRepository.value = JSON.parse(JSON.stringify(item))
}

const handleCancelDelete = () => {
  deleteRepository.value = undefined
}

const handleDeleted = async (id: string) => {
  await repositoriesStore.fetch()
  // if the deleted repository is the current repository, clear the state
  if (id === repositoriesStore.getCurrentId) {
    await clearState()
  }
  handleCancelDelete()
}

const handleUnlink = (item: Repository) => {
  unlinkRepository.value = JSON.parse(JSON.stringify(item))
}

const handleCancelUnlink = () => {
  unlinkRepository.value = undefined
}

const handleUnlinked = async (id: string) => {
  await repositoriesStore.fetch()
  // If the unlinked repository is the current repository, clear the state
  if (id === repositoriesStore.getCurrentId) {
    await clearState()
  }
  handleCancelUnlink()
}

const clearState = async () => {
  await Promise.all([
    analyticsStore.clear(false),
    mainStore.clearState(),
    repositoriesStore.defineCurrent(),
  ])
}

const handleClickUpgrade = () => {
  handleClose()
}
</script>

<style>
.modal-repositories {

  .ui-dialog__content {
    @apply min-h-96;
  }

  &__title {
    @apply flex items-center;
    @apply gap-4;
  }

  &__search {
    @apply flex items-center;
    @apply gap-1;
  }

  &__search-input {
    @apply flex-auto;
  }

  &__items {
    @apply flex flex-col flex-auto;
    @apply gap-2;
  }

  &__no-data {
    @apply flex-auto;
  }
}
</style>
